// ======================================
// Admin Design Ideas Page Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { designIdeasAPI } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderAdminDesignIdeas() {
  if (!requireAuth(['admin', 'manager', 'designer'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const content = `
          <div class="admin-toolbar" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--space-4);">
            <div class="filter-group">
                <select id="category-filter" class="form-select" style="width:200px;">
                    <option value="">All Categories</option>
                    <option value="living-room">Living Room</option>
                    <option value="bedroom">Bedroom</option>
                    <option value="kitchen">Kitchen</option>
                    <option value="bathroom">Bathroom</option>
                    <option value="dining">Dining</option>
                    <option value="kids-room">Kids Room</option>
                    <option value="pooja-room">Pooja Room</option>
                </select>
            </div>
            <button class="btn btn-primary btn-sm" id="add-idea-btn">
              ${icons.plus || '+'} Add Design Idea
            </button>
          </div>
          
          <div class="admin-card">
            <div class="admin-table-container">
              <table class="admin-table" id="ideas-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Style/Budget</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody id="ideas-body">
                  <tr><td colspan="5" class="loading-cell">Loading design ideas...</td></tr>
                </tbody>
              </table>
            </div>
          </div>
    
    <!-- Modal -->
    <div class="modal-overlay" id="idea-modal" style="display:none;">
      <div class="modal-dialog" style="max-width:600px;">
        <div class="modal-header">
          <h2 class="modal-title" id="modal-title">Add New Design Idea</h2>
          <button class="modal-close" data-close-modal>&times;</button>
        </div>
        <div class="modal-body" id="modal-body">
            <!-- Form injected here -->
        </div>
      </div>
    </div>
  `;

  return renderAdminLayout('design-ideas', 'Design Ideas', content);
}

export async function initAdminDesignIdeas() {
  initAdminLayout();

  // Filter
  const categoryFilter = document.getElementById('category-filter');
  if (categoryFilter) {
    categoryFilter.addEventListener('change', () => loadDesignIdeas(categoryFilter.value));
  }

  // Load Data
  loadDesignIdeas();

  // Add Button
  const addBtn = document.getElementById('add-idea-btn');
  if (addBtn) {
    addBtn.addEventListener('click', () => showIdeaForm());
  }

  // Close Modals
  document.querySelectorAll('[data-close-modal]').forEach(btn => {
    btn.addEventListener('click', closeModal);
  });
}

async function loadDesignIdeas(category = null) {
  const tbody = document.getElementById('ideas-body');
  if (!tbody) return;

  try {
    const params = category ? { category } : {};
    const response = await designIdeasAPI.list(params);
    const items = response.items || response.data || [];

    if (!Array.isArray(items) || items.length === 0) {
      tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:2rem;">No design ideas found. Add one!</td></tr>';
      return;
    }

    tbody.innerHTML = items.map(item => `
      <tr>
        <td>
            <img src="${item.image_url || 'https://via.placeholder.com/50'}" alt="${item.title}" style="width:60px;height:40px;object-fit:cover;border-radius:4px;">
        </td>
        <td>
            <div style="font-weight:600;">${item.title}</div>
            <div style="font-size:12px;color:var(--color-text-muted);display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden;">${item.description || ''}</div>
        </td>
        <td><span class="badge badge-outline">${item.category}</span></td>
        <td>
            <div style="font-size:12px;">${item.style || '-'}</div>
            <div style="font-size:12px;color:var(--color-text-secondary);">${item.budget || '-'}</div>
        </td>
        <td>
          <div class="action-buttons">
            <button class="btn-icon" onclick="window.editIdea(${item.id})" title="Edit">${icons.edit || '✎'}</button>
            <button class="btn-icon delete" onclick="window.deleteIdea(${item.id})" title="Delete">${icons.trash || '🗑️'}</button>
          </div>
        </td>
      </tr>
    `).join('');
  } catch (error) {
    console.error('Failed to load design ideas:', error);
    tbody.innerHTML = `<tr><td colspan="5" class="error-cell">Failed to load: ${error.message}</td></tr>`;
  }
}

function showIdeaForm(idea = null) {
  const modal = document.getElementById('idea-modal');
  const modalBody = document.getElementById('modal-body');
  const modalTitle = document.getElementById('modal-title');

  if (!modal || !modalBody) return;

  const isEdit = !!idea;
  modalTitle.textContent = isEdit ? 'Edit Design Idea' : 'Add New Design Idea';
  modal.style.display = 'flex';

  modalBody.innerHTML = `
    <form id="idea-form">
      <div class="form-grid">
        <div class="form-group full-width">
          <label class="form-label">Title *</label>
          <input type="text" name="title" class="form-input" required value="${idea?.title || ''}">
        </div>
        <div class="form-group">
          <label class="form-label">Category *</label>
          <select name="category" class="form-select" required>
            <option value="living-room" ${idea?.category === 'living-room' ? 'selected' : ''}>Living Room</option>
            <option value="bedroom" ${idea?.category === 'bedroom' ? 'selected' : ''}>Bedroom</option>
            <option value="kitchen" ${idea?.category === 'kitchen' ? 'selected' : ''}>Kitchen</option>
            <option value="bathroom" ${idea?.category === 'bathroom' ? 'selected' : ''}>Bathroom</option>
            <option value="dining" ${idea?.category === 'dining' ? 'selected' : ''}>Dining</option>
            <option value="kids-room" ${idea?.category === 'kids-room' ? 'selected' : ''}>Kids Room</option>
            <option value="pooja-room" ${idea?.category === 'pooja-room' ? 'selected' : ''}>Pooja Room</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">Style</label>
          <input type="text" name="style" class="form-input" value="${idea?.style || ''}" placeholder="e.g. Modern">
        </div>
        <div class="form-group">
          <label class="form-label">Budget</label>
           <select name="budget" class="form-select">
            <option value="Economy" ${idea?.budget === 'Economy' ? 'selected' : ''}>Economy</option>
            <option value="Premium" ${idea?.budget === 'Premium' ? 'selected' : ''}>Premium</option>
            <option value="Luxury" ${idea?.budget === 'Luxury' ? 'selected' : ''}>Luxury</option>
          </select>
        </div>
        <div class="form-group full-width">
          <label class="form-label">Image URL *</label>
          <input type="url" name="image_url" class="form-input" required value="${idea?.image_url || ''}" placeholder="https://...">
        </div>
        <div class="form-group full-width">
          <label class="form-label">Description</label>
          <textarea name="description" class="form-textarea" rows="3">${idea?.description || ''}</textarea>
        </div>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" onclick="window.closeModal()">Cancel</button>
        <button type="submit" class="btn btn-primary">${isEdit ? 'Update' : 'Create'}</button>
      </div>
    </form>
  `;

  // Submit Handler
  const form = document.getElementById('idea-form');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    btn.disabled = true;
    btn.textContent = 'Saving...';

    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    try {
      if (isEdit) {
        await designIdeasAPI.update(idea.id, data);
      } else {
        await designIdeasAPI.create(data);
      }
      closeModal();
      loadDesignIdeas(document.getElementById('category-filter')?.value);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to save');
      btn.disabled = false;
      btn.textContent = isEdit ? 'Update' : 'Create';
    }
  });
}

export function closeModal() {
  const modal = document.getElementById('idea-modal');
  if (modal) modal.style.display = 'none';
}

// Global functions for inline usage
window.closeModal = closeModal;
window.editIdea = async (id) => {
  try {
    const idea = await designIdeasAPI.get(id);
    showIdeaForm(idea);
  } catch (e) {
    alert('Failed to load details');
  }
};
window.deleteIdea = async (id) => {
  if (confirm('Are you sure you want to delete this design idea?')) {
    try {
      await designIdeasAPI.delete(id);
      loadDesignIdeas(document.getElementById('category-filter')?.value);
    } catch (e) {
      alert('Failed to delete');
    }
  }
};
